import React from 'react';

import { FormattedMessage } from 'react-intl';

import AvatarGroup from '@atlaskit/avatar-group/avatar-group';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Text } from '@atlaskit/primitives/compiled/text';
import Skeleton from '@atlaskit/skeleton';

import type { ConfluencePageContributor } from '../../../../../api/useTwgSources/gql/types';
import { messages } from '../../messages';

const DEFAULT_MAX_CONTRIBUTORS_IN_STACK = 3;

const styles = cssMap({
	pressable: {
		backgroundColor: 'transparent',
	},
	avatarGroup: {
		transform: 'scale(0.75)',
	},
});

export const ContributorAvatarStack = ({
	contributors,
	onClick,
	isLoading,
	singleColumnLayout = false,
}: {
	contributors: ConfluencePageContributor[];

	onClick: (event: React.MouseEvent<Element, MouseEvent>) => void;
	isLoading?: boolean;
	singleColumnLayout?: boolean;
}): React.JSX.Element => {
	if (isLoading) {
		return (
			<Skeleton
				height="20px"
				width="100px"
				isShimmering
				testId="contributors-avatar-stack-skeleton"
			/>
		);
	}

	const maxContributors = DEFAULT_MAX_CONTRIBUTORS_IN_STACK;

	// Transform to avatar group data format
	const avatarData = contributors.map((contributor, index) => ({
		key: contributor.data.accountId,
		name: contributor.data.name,
		src: contributor.data.picture,
		appearance: 'circle' as const,
		testId: `contributor-avatar-${index}`,
	}));

	const remainingContributorsInStack = singleColumnLayout
		? avatarData.slice(1, 3)
		: avatarData.slice(3, 6);
	const remainingContributorsCount = singleColumnLayout
		? avatarData.slice(1).length
		: avatarData.slice(3).length;

	return (
		<Pressable onClick={onClick} xcss={styles.pressable}>
			<Inline alignBlock="center" space="space.0">
				<Box xcss={styles.avatarGroup}>
					<AvatarGroup
						appearance="stack"
						data={remainingContributorsInStack}
						size="small"
						maxCount={maxContributors}
						isTooltipDisabled={true}
						testId="contributors-avatargroup"
					/>
				</Box>
				{remainingContributorsInStack.length > 0 && (
					<Text color="color.text.subtle" testId="contributors-avatar-stack-remaining-count">
						<FormattedMessage
							{...messages.contributors_avatar_stack_text}
							values={{ count: remainingContributorsCount }}
						/>
					</Text>
				)}
			</Inline>
		</Pressable>
	);
};
